import React, { useState, useEffect, useRef, useCallback } from "react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  buildDisplayNameFromUser,
  buildDisplayNameFromMaybeObject,
  resolveDisplayName,
} from "../../lib/displayName";
import { Clipboard, Save, X, User, Monitor, Calendar, FileText, ChevronRight, ChevronDown, Loader2, Clock, Wrench, PenTool, Maximize2, Layers } from 'lucide-react';
import SearchableSelect from "../ui/SearchableSelect";

export default function OrdenFormMobile({ orden, onClose, onSaved, agendamientoInicial }) {
  const { user } = useAuth();
  const currentUserDisplayName = buildDisplayNameFromUser(user);
  
  // Estados para secciones colapsables en móvil
  const [seccionAbierta, setSeccionAbierta] = useState('cliente');
  const [guardando, setGuardando] = useState(false);
  
  // Referencias y estados para firma táctil
  const canvasRef = useRef(null);
  const fullCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [firmaFullscreen, setFirmaFullscreen] = useState(false);
  const [fullScreenDims, setFullScreenDims] = useState({ width: 0, height: 0 });
  
  const secciones = {
    cliente: "Cliente y Fechas",
    equipos: "Equipos del Servicio", 
    trabajo: "Trabajo Realizado",
    tecnico: "Técnico Asignado",
    firma: "Firma del Cliente"
  };

  // Estados del formulario
  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [todosLosEquipos, setTodosLosEquipos] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [departamentosDisponibles, setDepartamentosDisponibles] = useState([]);
  const [cargaInicialCompleta, setCargaInicialCompleta] = useState(false);
  const [agendamientos, setAgendamientos] = useState([]);
  const [cargandoAgendamientos, setCargandoAgendamientos] = useState(false);
  const [ticketsCliente, setTicketsCliente] = useState([]);
  const [cargandoTickets, setCargandoTickets] = useState(false);

  // Helpers de fecha / timezone
  const getLocalDateString = (date) => {
    let d;
    if (date) {
      d = new Date(date);
    } else {
      // Si no se provee fecha, usar la actual de Paraguay explícitamente
      try {
        d = new Date(new Date().toLocaleString("en-US", {timeZone: "America/Asuncion"}));
      } catch (e) {
        console.warn("Error setting Paraguay timezone, falling back to local", e);
        d = new Date();
      }
    }

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const parseISOToLocalDate = (iso) => {
    if (!iso) return getLocalDateString();
    const d = new Date(iso);
    const offset = d.getTimezoneOffset();
    const correctedDate = new Date(d.getTime() + offset * 60000);
    return getLocalDateString(correctedDate);
  };

  const parseISOToLocalTime = (iso) => {
    if (!iso) return "";
    
    if (typeof iso === 'string' && /^\d{1,2}:\d{2}$/.test(iso)) {
      return iso.length === 4 ? '0' + iso : iso;
    }
    
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) {
        if (typeof iso === 'string' && /^\d{1,2}:\d{2}/.test(iso)) {
          return iso.substring(0, 5);
        }
        return "";
      }
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${hh}:${mm}`;
    } catch (error) {
      if (typeof iso === 'string' && /^\d{1,2}:\d{2}/.test(iso)) {
        return iso.substring(0, 5);
      }
      return "";
    }
  };

  const generateOrderNumber = () => {
    const date = getLocalDateString();
    const suffix = Math.random().toString(36).substring(2, 10);
    return `OS-${date.replace(/-/g, "")}-${suffix}`;
  };

  const deriveNombreTecnico = useCallback(
    (ordenData) =>
      resolveDisplayName({
        primary: ordenData?.nombre_tecnico,
        secondary: [
          ordenData?.tecnico_nombre,
          ordenData?.tecnico,
          ordenData?.tecnico_detalle,
          ordenData?.usuario_creador_nombre,
          ordenData?.usuario_creador,
        ],
        lookup: () => {
          if (!ordenData?.tecnico_id) return "";
          if (
            user &&
            (ordenData.tecnico_id === user?._id || ordenData.tecnico_id === user?.id)
          ) {
            return currentUserDisplayName;
          }
          const candidato = tecnicos.find(
            (t) => t.id === ordenData.tecnico_id || t._id === ordenData.tecnico_id
          );
          return candidato ? buildDisplayNameFromUser(candidato) : "";
        },
      }),
    [currentUserDisplayName, tecnicos, user]
  );

  const buildFormState = useCallback((ordenData) => {
    if (!ordenData) {
      const nombreTecnicoPorDefecto =
        (user?.rol || "").toLowerCase() === "tecnico"
          ? currentUserDisplayName
          : "";
      return {
        numero_orden: generateOrderNumber(),
        cliente_id: "",
        nombre_cliente: "",
        sucursal: "",
        tipo_servicio: "mantenimiento_preventivo",
        es_mantenimiento_preventivo: true,
        tecnico_id: (user?.rol || "").toLowerCase() === "tecnico" ? user?.id || user?._id : "",
        nombre_tecnico: nombreTecnicoPorDefecto,
        tecnico_nombre: nombreTecnicoPorDefecto,
        fecha: getLocalDateString(),
        hora_inicio: parseISOToLocalTime(new Date().toISOString()),
        hora_fin: "",
        equipos_ids: [],
        equipo_descripcion: "",
        trabajos_realizados: "",
        observaciones: "",
        firma_cliente: "",
        firma_aclaracion: "",
        estado: "borrador",
        es_servicio_programado: false,
        agendamiento_id: "",
        departamentos: []
      };
    }

    if (ordenData) {
      const nombreTecnicoExistente =
        deriveNombreTecnico(ordenData) ||
        buildDisplayNameFromMaybeObject(ordenData.nombre_tecnico) ||
        buildDisplayNameFromMaybeObject(ordenData.tecnico_nombre) ||
        "";
      const clienteRaw = ordenData.cliente || {};
      const clienteId = ordenData.cliente_id || clienteRaw._id || clienteRaw.id || "";
      const clienteNombre = ordenData.nombre_cliente || clienteRaw.nombre || "";
      const sucursal = ordenData.sucursal || clienteRaw.sucursal || "";
      // Solo cargar firma si viene de la orden específica, NO del perfil de cliente
      const firma = ordenData.firma_cliente || "";
      // Aclaración también específica de la orden
      const aclaracionFirma = ordenData.firma_aclaracion || ordenData.aclaracion_firma_cliente || "";
      const equiposSeleccionados = (() => {
        if (Array.isArray(ordenData.equipos_ids) && ordenData.equipos_ids.length > 0) return ordenData.equipos_ids;
        if (Array.isArray(ordenData.equipos) && ordenData.equipos.length > 0) {
          return ordenData.equipos.map(eq => eq._id || eq.id).filter(Boolean);
        }
        return [];
      })();
      const trabajos = ordenData.trabajos_realizados || ordenData.trabajo_realizado || "";
      const obs = ordenData.observaciones || ordenData.observaciones_tecnico || "";

      return {
        cliente_id: clienteId,
        nombre_cliente: clienteNombre,
        sucursal,
        tipo_servicio: ordenData.tipo_servicio || "mantenimiento_preventivo",
        es_mantenimiento_preventivo: ordenData.es_mantenimiento_preventivo !== undefined ? ordenData.es_mantenimiento_preventivo : true,
        tecnico_id: ordenData.tecnico_id || (user?.id || user?._id) || "",
  nombre_tecnico: nombreTecnicoExistente,
  tecnico_nombre: nombreTecnicoExistente,
        fecha: parseISOToLocalDate(ordenData.fecha || ordenData.fecha_programada),
        hora_inicio: parseISOToLocalTime(ordenData.hora_inicio || ordenData.fecha_inicio),
        hora_fin: parseISOToLocalTime(ordenData.hora_fin || ordenData.fecha_fin),
        equipos_ids: equiposSeleccionados,
        equipo_descripcion: ordenData.equipo_descripcion || ordenData.trabajo_realizado || "",
        trabajos_realizados: trabajos,
        observaciones: obs,
        firma_cliente: firma,
        firma_aclaracion: aclaracionFirma,
        estado: ordenData.estado || "borrador",
        es_servicio_programado: ordenData.es_servicio_programado || false,
        agendamiento_id: ordenData.agendamiento_id || "",
        numero_orden: ordenData.numero_orden || generateOrderNumber(),
        origen: ordenData.origen || "interno",
        ticket_origen_id: ordenData.ticket_origen_id || "",
        departamentos: ordenData.departamentos || []
      };
    }

    return {
      numero_orden: generateOrderNumber(),
      cliente_id: "",
      nombre_cliente: "",
      sucursal: "",
      tipo_servicio: "mantenimiento_preventivo",
      es_mantenimiento_preventivo: true,
      tecnico_id: (user?.rol || "").toLowerCase() === "tecnico" ? user?.id || user?._id : "",
      nombre_tecnico: (user?.rol || "").toLowerCase() === "tecnico" ? currentUserDisplayName : "",
      tecnico_nombre: (user?.rol || "").toLowerCase() === "tecnico" ? currentUserDisplayName : "",
      fecha: getLocalDateString(),
      hora_inicio: parseISOToLocalTime(new Date().toISOString()),
      hora_fin: "",
      equipos_ids: [],
      equipo_descripcion: "",
      trabajos_realizados: "",
      observaciones: "",
      firma_cliente: "",
      firma_aclaracion: "",
      estado: "borrador",
      es_servicio_programado: false,
      agendamiento_id: "",
      origen: "interno",
      ticket_origen_id: "",
      departamentos: []
    };
  }, [currentUserDisplayName, deriveNombreTecnico, user]);

  const [form, setForm] = useState(() => buildFormState(orden));

  useEffect(() => {
    cargarDatos();
  }, []);

  // Ref para almacenar firma cargada del detalle completo (sobrevive a re-renders de buildFormState)
  const firmaDetalleRef = useRef({ firma_cliente: null, firma_aclaracion: null });

  // Cargar detalle completo con firmas cuando se edita una orden existente
  // El listado excluye firma_cliente, firma_aclaracion, etc. para ahorrar ancho de banda
  useEffect(() => {
    if (!orden) return;
    const ordenId = orden._id || orden.id;
    if (!ordenId) return;
    // Si ya tiene firma_cliente definida (no undefined), no necesita recargar
    if (orden.firma_cliente !== undefined) {
      firmaDetalleRef.current = {
        firma_cliente: orden.firma_cliente || "",
        firma_aclaracion: orden.firma_aclaracion || orden.aclaracion_firma_cliente || orden.aclaracion_firma || "",
      };
      return;
    }

    const cargarDetalleCompleto = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await http.get(`/ordenes/${ordenId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const detalle = res.data;
        const firmaData = {
          firma_cliente: detalle.firma_cliente || "",
          firma_aclaracion: detalle.firma_aclaracion || detalle.aclaracion_firma_cliente || detalle.aclaracion_firma || "",
        };
        firmaDetalleRef.current = firmaData;
        // Actualizar solo los campos de firma en el form sin perder el resto
        setForm(prev => ({
          ...prev,
          ...firmaData,
        }));
      } catch (err) {
        console.error("Error cargando detalle completo de orden (firmas):", err);
      }
    };
    cargarDetalleCompleto();
  }, [orden]);

  useEffect(() => {
    const newState = buildFormState(orden);
    // Preservar firma si tenemos datos cargados del detalle completo
    // (buildFormState lee de orden prop que puede no tener firma)
    if (firmaDetalleRef.current.firma_cliente !== null) {
      newState.firma_cliente = firmaDetalleRef.current.firma_cliente;
      newState.firma_aclaracion = firmaDetalleRef.current.firma_aclaracion;
    }
    setForm(newState);

    const clienteId = orden?.cliente_id || orden?.cliente?._id || orden?.cliente?.id;
    if (clienteId) {
      cargarEquiposDelCliente(clienteId);
      // Cargar departamentos del cliente
      const token = localStorage.getItem('token');
      http.get(`/clientes/${clienteId}/departamentos`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => setDepartamentosDisponibles(Array.isArray(r.data) ? r.data : [])).catch(() => setDepartamentosDisponibles([]));
      // Cargar tickets si es modo edición y el origen es ticket
      if (orden?.origen === 'ticket') {
         cargarTickets(clienteId);
      }
      // Cargar agendamientos si es servicio programado
      if (orden?.es_servicio_programado && orden?.agendamiento_id) {
         cargarAgendamientos(clienteId);
      }
    } else {
      setEquipos([]);
    }
  }, [orden, buildFormState]);

  useEffect(() => {
    if (!form.tecnico_id) return;
    if (!tecnicos.length && (user?.rol || "").toLowerCase() !== "tecnico") return;

    setForm((prev) => {
      if (!prev.tecnico_id) return prev;
      const matchingTecnico = tecnicos.find(
        (t) => t.id === prev.tecnico_id || t._id === prev.tecnico_id
      );

      if (!matchingTecnico) {
        if (
          (user?.rol || "").toLowerCase() === "tecnico" &&
          (prev.tecnico_id === user?._id || prev.tecnico_id === user?.id)
        ) {
          if (prev.nombre_tecnico === currentUserDisplayName) return prev;
          return {
            ...prev,
            nombre_tecnico: currentUserDisplayName,
            tecnico_nombre: currentUserDisplayName,
          };
        }
        return prev;
      }

      const display = buildDisplayNameFromUser(matchingTecnico);
      if (display && display !== prev.nombre_tecnico) {
        return {
          ...prev,
          nombre_tecnico: display,
          tecnico_nombre: display,
        };
      }
      return prev;
    });
  }, [currentUserDisplayName, form.tecnico_id, tecnicos, user]);

  // Efecto para filtrar equipos cuando se carguen y ya hay una sucursal o departamentos seleccionados
  useEffect(() => {
    if (todosLosEquipos.length > 0) {
      filtrarEquiposPorSucursal(form.sucursal, form.departamentos);
    }
  }, [todosLosEquipos]);

  async function cargarClientesEquiposUsuarios() {
    const token = localStorage.getItem("token");
    try {
      const clientesRes = await http.get("/clientes", { headers: { Authorization: `Bearer ${token}` } });
      const clientesOrdenados = (clientesRes.data || []).sort((a, b) => 
        (a.nombre || "").toLowerCase().localeCompare((b.nombre || "").toLowerCase())
      );
      setClientes(clientesOrdenados);
    } catch (err) {
      console.error("Error cargando clientes", err);
      setClientes([]);
    }

    if ((user?.rol || "").toLowerCase() === "admin") {
      try {
        const tecnicosRes = await http.get("/usuarios", { headers: { Authorization: `Bearer ${token}` }, params: { rol: "tecnico", activo: true } });
        const tecnicosSolo = (tecnicosRes.data || []).filter(
          (u) => (u.rol || "").toLowerCase() === "tecnico"
        );
        const ordenados = tecnicosSolo.sort((a, b) => {
          const nombreA = buildDisplayNameFromUser(a) || a.username || "";
          const nombreB = buildDisplayNameFromUser(b) || b.username || "";
          return nombreA.localeCompare(nombreB);
        });
        setTecnicos(ordenados);
      } catch (err) {
        console.error("Error cargando técnicos", err);
        setTecnicos([]);
      }
    } else if (user) {
      setTecnicos([user]);
    }
  }

  async function cargarEquiposDelCliente(clienteId) {
    try {
      if (!clienteId) return setEquipos([]);
      
      const token = localStorage.getItem("token");
      if (!clienteId) {
        console.log("ID de cliente inválido:", clienteId);
        setEquipos([]);
        setTodosLosEquipos([]);
        return;
      }
      
      console.log("🛠️ Cargando equipos para cliente:", clienteId);
      
      // Cargar TODOS los equipos (igual que Desktop y PresupuestoForm)
      const res = await http.get("/equipos/", {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("🛠️ Total equipos en sistema:", res.data?.length || 0);
      
      const todosEquipos = res.data || [];
      
      // Filtrar equipos que pertenecen al cliente (misma lógica que Desktop)
      const equiposDelCliente = todosEquipos.filter(equipo => {
        // Buscar por cliente_id (UUID o ObjectId)
        const coincidePorId = equipo.cliente_id === clienteId || 
                             equipo.cliente?._id === clienteId ||
                             equipo.cliente?.id === clienteId;
        
        // Buscar por nombre del cliente (para equipos con IDs antiguos)
        const clienteSeleccionado = clientes.find(c => c.id === clienteId || c._id === clienteId);
        const coincidePorNombre = clienteSeleccionado && 
                                 (equipo.cliente?.nombre === clienteSeleccionado.nombre ||
                                  (equipo.cliente_nombre && equipo.cliente_nombre === clienteSeleccionado.nombre));
        
        return coincidePorId || coincidePorNombre;
      });
      
      console.log("🛠️ Equipos del cliente encontrados:", equiposDelCliente.length);
      
      setTodosLosEquipos(equiposDelCliente);
      setEquipos(equiposDelCliente);
    } catch (err) {
      console.error("❌ Error cargando equipos:", err);
      setEquipos([]);
      setTodosLosEquipos([]);
    }
  }

  async function cargarSucursales(clienteId) {
    try {
      if (!clienteId) {
        console.log("📍 Cliente ID vacío, limpiando sucursales");
        setSucursales([]);
        return;
      }
      const token = localStorage.getItem("token");
      console.log("📍 INICIO cargarSucursales para cliente:", clienteId);
      
      const res = await http.get(`/clientes/${clienteId}/sucursales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("📍 Sucursales recibidas:", res.data);
      // Filtrar "Principal" si viene del backend
      const sucursalesLimpias = (res.data || []).filter(s => s !== "Principal");
      setSucursales(sucursalesLimpias);
    } catch (err) {
      console.error("❌ Error cargando sucursales:", err);
      // Fallback: derivar sucursales en frontend desde cliente
      try {
        const clienteSel = clientes.find(c => c._id === clienteId || c.id === clienteId);
        const base = new Set();
        if (clienteSel && Array.isArray(clienteSel.sucursales)) {
          // Normalizar: sucursales pueden ser strings o objetos {nombre: "..."}
          clienteSel.sucursales.forEach(s => {
            if (typeof s === 'string' && s.trim()) base.add(s.trim());
            else if (s && typeof s === 'object' && s.nombre) base.add(s.nombre);
          });
        }
        // Complementar con sucursales de equipos ya cargados
        todosLosEquipos.filter(eq => (eq.cliente_id === clienteId) || (eq.cliente?.id === clienteId) || (eq.cliente?._id === clienteId))
          .map(eq => eq.sucursal)
          .filter(Boolean)
          .forEach(s => base.add(s));
        const lista = Array.from(base).filter(s => s !== "Principal");
        // if (lista.length === 0) lista.push("Principal"); // Eliminado para evitar sucursal fantasma
        setSucursales(lista);
        console.warn("📍 Usando sucursales derivadas en frontend (filtrado Principal):", lista);
      } catch (fErr) {
        console.warn("📍 Fallback de sucursales no disponible", fErr);
        setSucursales([]);
      }
    }
  }

  // Función para filtrar equipos por sucursal y departamentos seleccionados
  function filtrarEquiposPorSucursal(sucursal, departamentos) {
    const depts = departamentos || form.departamentos || [];
    let filtrados = todosLosEquipos;
    
    if (sucursal) {
      filtrados = filtrados.filter(eq => eq.sucursal === sucursal);
    }
    if (depts.length > 0) {
      filtrados = filtrados.filter(eq => !eq.departamento || depts.includes(eq.departamento));
    }
    
    // Siempre incluir equipos ya seleccionados para no perderlos
    const selectedIds = form.equipos_ids || [];
    if (selectedIds.length > 0) {
      const filtradosIds = new Set(filtrados.map(eq => eq._id || eq.id));
      const seleccionadosFaltantes = todosLosEquipos.filter(eq => {
        const eqId = eq._id || eq.id;
        return selectedIds.includes(eqId) && !filtradosIds.has(eqId);
      });
      filtrados = [...filtrados, ...seleccionadosFaltantes];
    }
    
    setEquipos(filtrados);
  }

  const cargarDatos = async () => {
    await cargarClientesEquiposUsuarios();
    setCargaInicialCompleta(true);
  };

  // Efecto para pre-llenar el formulario desde un agendamiento
  useEffect(() => {
    if (!agendamientoInicial || orden) return; // Solo para órdenes nuevas
    if (!cargaInicialCompleta) return;
    
    const ag = agendamientoInicial;
    const clienteId = ag.cliente_id || ag.cliente?._id || ag.cliente?.id || '';
    const clienteNombre = ag.cliente?.nombre || ag.nombre_cliente || '';
    
    const tipoMap = {
      'preventivo': 'mantenimiento_preventivo',
      'correctivo': 'mantenimiento_correctivo',
      'revision': 'revision',
      'limpieza': 'limpieza',
      'actualizacion': 'actualizacion',
      'presupuesto': 'presupuesto'
    };
    const tipoServicio = tipoMap[ag.tipo_mantenimiento] || 'mantenimiento_preventivo';
    
    const equiposIds = Array.isArray(ag.equipos) 
      ? ag.equipos.map(eq => eq._id || eq.id).filter(Boolean)
      : Array.isArray(ag.equipos_ids) ? ag.equipos_ids : [];
    
    console.log('📋 [Mobile] Pre-llenando formulario desde agendamiento:', ag.titulo);
    
    setForm(prev => ({
      ...prev,
      cliente_id: clienteId,
      nombre_cliente: clienteNombre,
      sucursal: ag.sucursal || '',
      tipo_servicio: tipoServicio,
      es_mantenimiento_preventivo: tipoServicio === 'mantenimiento_preventivo',
      fecha: ag.fecha_programada ? ag.fecha_programada.split('T')[0] : prev.fecha,
      hora_inicio: ag.hora_inicio ? ag.hora_inicio.substring(0, 5) : prev.hora_inicio,
      hora_fin: ag.hora_fin ? ag.hora_fin.substring(0, 5) : '',
      equipos_ids: equiposIds,
      descripcion_problema: ag.descripcion || '',
      observaciones: ag.observaciones || '',
      es_servicio_programado: true,
      agendamiento_id: ag._id || ag.id || '',
      tecnico_id: ag.tecnico_asignado_id || prev.tecnico_id || '',
      nombre_tecnico: ag.tecnico_asignado || prev.nombre_tecnico || '',
      tecnico_nombre: ag.tecnico_asignado || prev.tecnico_nombre || '',
    }));
    
    if (clienteId) {
      Promise.all([
        cargarSucursales(clienteId),
        cargarEquiposDelCliente(clienteId),
        cargarAgendamientos(clienteId)
      ]).catch(err => console.warn('⚠️ Error cargando datos desde agendamiento:', err));
    }
  }, [agendamientoInicial, orden, cargaInicialCompleta]);

  const handleClienteChange = async (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId || c._id === clienteId);
    setForm(prev => ({
      ...prev,
      cliente_id: clienteId,
      nombre_cliente: cliente?.nombre || "",
      equipos_ids: [],
      sucursal: "",
      departamentos: [],
      // Reset origen a interno si se deselecciona el cliente
      origen: clienteId ? prev.origen : "interno",
      ticket_origen_id: ""
    }));
    
    // Cargar sucursales, equipos y tickets en paralelo
    setDepartamentosDisponibles([]);
    await Promise.all([
      cargarSucursales(clienteId),
      cargarEquiposDelCliente(clienteId),
      cargarTickets(clienteId),
      clienteId ? http.get(`/clientes/${clienteId}/departamentos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(r => setDepartamentosDisponibles(Array.isArray(r.data) ? r.data : [])).catch(() => setDepartamentosDisponibles([])) : Promise.resolve()
    ]);
  };

  const getTimezoneOffsetString = () => {
    const offsetMinutes = -new Date().getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMinutes);
    const hours = String(Math.floor(abs / 60)).padStart(2, "0");
    const minutes = String(abs % 60).padStart(2, "0");
    return `${sign}${hours}:${minutes}`;
  };

  // Función para verificar si el canvas está vacío (todo blanco)
  const canvasEstaVacio = (canvas) => {
    if (!canvas) return true;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Recorrer píxeles. Si encontramos uno oscuro, hay dibujo.
    // data[i]=R, data[i+1]=G, data[i+2]=B
    // Como el fondo es blanco (255,255,255), buscamos algo significativamente más oscuro.
    for (let i = 0; i < data.length; i += 4) {
      if (data[i] < 240 || data[i + 1] < 240 || data[i + 2] < 240) {
        return false; // Encontró un píxel no blanco => Hay dibujo
      }
    }
    return true; // Todo blanco
  };

  async function guardarOrden() {
    // Validación: Si hay sucursales disponibles, es obligatorio seleccionar una
    if (sucursales.length > 0 && !form.sucursal) {
      alert("El cliente tiene sucursales registradas. Por favor seleccione una.");
      return;
    }

    // Calcular firma actual real
    let firmaActual = form.firma_cliente;
    
    // Si la sección de firma está activa, la verdad absoluta la tiene el canvas visual
    if (seccionAbierta === 'firma' && canvasRef.current) {
        if (canvasEstaVacio(canvasRef.current)) {
            // Si el canvas está visualmente vacío, entonces NO hay firma, aunque el estado diga lo contrario
            firmaActual = ""; 
        } else {
            // Si tiene dibujo, tomamos el dataURL fresco
            firmaActual = canvasRef.current.toDataURL('image/png');
        }
    }

    // --- VALIDACIÓN DE FIRMA Y ACLARACIÓN ---
    // Detectar si es servicio remoto (case-insensitive)
    const esServicioRemoto = form.tipo_servicio 
      ? form.tipo_servicio.toLowerCase().includes('remoto') 
      : false;

    // Si intenta completar la orden y NO es servicio remoto
    if (form.estado === 'completada' && !esServicioRemoto) {
      // Usamos firmaActual calculada arriba
      if (!firmaActual || firmaActual.trim() === '') {
        alert('🛑 FIRMA OBLIGATORIA\n\nEste no es un servicio remoto. Debe solicitar la firma del cliente para cerrar la orden.\n\nPor favor dibuje la firma en el recuadro.');
        setSeccionAbierta('firma'); // Abrir sección de firma automáticamente
        return;
      }

      if (!form.firma_aclaracion || form.firma_aclaracion.trim() === '') {
        alert('🛑 ACLARACIÓN OBLIGATORIA\n\nPor favor escriba el Nombre y Apellido de la persona que firma.');
        setSeccionAbierta('firma');
        // Scroll al input si es posible (no tenemos refs directas aquí fácil, pero abrir la sección ayuda)
        return;
      }
    }
    // ----------------------------------------------

    setGuardando(true);
    
    try {
      const token = localStorage.getItem("token");

      // Crear payload asegurando que usamos la firma validada
      const payload = { ...form, firma_cliente: firmaActual };

      // Mapear firma_aclaracion a aclaracion_firma para consistencia con backend
      if (payload.firma_aclaracion) {
        payload.aclaracion_firma = payload.firma_aclaracion;
        // Si existia mapeo viejo, borrarlo o dejarlo caer.
        delete payload.aclaracion_firma_cliente;
      }

      // Si es nueva orden, generar número de orden en frontend
      if (!orden) {
        payload.numero_orden = generateOrderNumber();
      } else {
        if (orden.numero_orden) payload.numero_orden = orden.numero_orden;
      }

      if (!payload.tecnico_id && (user?.rol || "").toLowerCase() === "tecnico") {
        payload.tecnico_id = user?._id || user?.id || payload.tecnico_id;
      }

      const tecnicoSeleccionado = tecnicos.find(
        (t) =>
          t.id === payload.tecnico_id ||
          t._id === payload.tecnico_id ||
          t.id === form.tecnico_id ||
          t._id === form.tecnico_id
      );

      const nombreTecnicoFinal = resolveDisplayName({
        primary: payload.nombre_tecnico,
        secondary: [
          payload.tecnico_nombre,
          tecnicoSeleccionado,
          user &&
            (payload.tecnico_id === user?._id || payload.tecnico_id === user?.id)
            ? user
            : null,
        ],
        lookup: () => {
          if (tecnicoSeleccionado) {
            return buildDisplayNameFromUser(tecnicoSeleccionado);
          }
          if (
            user &&
            (payload.tecnico_id === user?._id || payload.tecnico_id === user?.id)
          ) {
            return currentUserDisplayName;
          }
          return "";
        },
      });

      if (nombreTecnicoFinal) {
        payload.nombre_tecnico = nombreTecnicoFinal;
        payload.tecnico_nombre = nombreTecnicoFinal;
      }

      const tz = getTimezoneOffsetString();

      // Convertir fecha y horas a formato ISO
      if (payload.fecha) {
        payload.fecha = `${payload.fecha}T00:00:00${tz}`;
      }

      if (form.hora_inicio) {
        const fechaBase = payload.fecha ? payload.fecha.slice(0, 10) : getLocalDateString();
        payload.hora_inicio = `${fechaBase}T${form.hora_inicio}:00${tz}`;
      } else {
        payload.hora_inicio = payload.hora_inicio || "";
      }

      if (form.hora_fin) {
        const fechaBase = payload.fecha ? payload.fecha.slice(0, 10) : getLocalDateString();
        payload.hora_fin = `${fechaBase}T${form.hora_fin}:00${tz}`;
      } else {
        payload.hora_fin = payload.hora_fin || "";
      }

      // Mantener el identificador seleccionado para el cliente
      if (payload.cliente_id) {
        payload.cliente_id = String(payload.cliente_id);
      }

      console.log('📤 ENVIANDO AL SERVIDOR:', {
        metodo: orden ? 'PUT' : 'POST',
        url: orden ? `/ordenes/${orden._id}` : '/ordenes',
        payload: payload
      });
      
      let response;
      if (orden) {
        response = await http.put(`/ordenes/${orden._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        response = await http.post("/ordenes", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      
      console.log('📥 RESPUESTA DEL SERVIDOR:', response.data);

      if (onSaved) onSaved();
      if (onClose) onClose();
    } catch (err) {
      console.error("Error guardando orden", err);
      alert("Error al guardar la orden. Revisa la consola.");
    } finally {
      setGuardando(false);
    }
  }

  // Funciones para firma táctil
  const getEventPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getEventPos(e);
    setLastPos(pos);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getEventPos(e);
    
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    
    setLastPos(pos);
  };

  const stopDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(false);
    
    // Auto-guardar firma en el estado al terminar de dibujar
    const canvas = canvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL('image/png');
      setForm(prev => ({ ...prev, firma_cliente: dataUrl }));
    }
  };

  const limpiarFirma = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    const fullCanvas = fullCanvasRef.current;
    if (fullCanvas) {
      const ctx = fullCanvas.getContext('2d');
      ctx.clearRect(0, 0, fullCanvas.width, fullCanvas.height);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, fullCanvas.width, fullCanvas.height);
    }
    setForm({ ...form, firma_cliente: "" });
  };

  // --- Fullscreen firma ---
  const getFullEventPos = (e) => {
    const canvas = fullCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawingFull = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getFullEventPos(e);
    setLastPos(pos);
  };

  const drawFull = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = fullCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getFullEventPos(e);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setLastPos(pos);
  };

  const stopDrawingFull = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const abrirFirmaFullscreen = async () => {
    setFirmaFullscreen(true);
    document.body.style.overflow = 'hidden';
    // Intentar forzar landscape en móviles
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen();
      }
      if (screen.orientation && screen.orientation.lock) {
        await screen.orientation.lock('landscape');
      }
    } catch (e) {
      // No pasa nada si falla, funciona igual
      console.log('Orientation lock no disponible:', e.message);
    }
  };

  const cerrarFirmaFullscreen = (guardar) => {
    if (guardar && fullCanvasRef.current) {
      // Extraer solo el contenido dibujado (bounding box de píxeles no blancos)
      const srcCanvas = fullCanvasRef.current;
      const ctx = srcCanvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, srcCanvas.width, srcCanvas.height);
      const data = imageData.data;

      let minX = srcCanvas.width, minY = srcCanvas.height, maxX = 0, maxY = 0;
      let hasContent = false;

      for (let y = 0; y < srcCanvas.height; y++) {
        for (let x = 0; x < srcCanvas.width; x++) {
          const i = (y * srcCanvas.width + x) * 4;
          // Pixel no blanco (R<250 OR G<250 OR B<250)
          if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) {
            hasContent = true;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }

      if (hasContent) {
        // Agregar padding alrededor del contenido
        const pad = 15;
        minX = Math.max(0, minX - pad);
        minY = Math.max(0, minY - pad);
        maxX = Math.min(srcCanvas.width - 1, maxX + pad);
        maxY = Math.min(srcCanvas.height - 1, maxY + pad);

        const cropW = maxX - minX + 1;
        const cropH = maxY - minY + 1;

        // Crear canvas recortado solo con la firma
        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = cropW;
        cropCanvas.height = cropH;
        const cropCtx = cropCanvas.getContext('2d');
        cropCtx.fillStyle = '#ffffff';
        cropCtx.fillRect(0, 0, cropW, cropH);
        cropCtx.drawImage(srcCanvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);

        const dataUrl = cropCanvas.toDataURL('image/png');
        setForm(prev => ({ ...prev, firma_cliente: dataUrl }));
      }
      // Si no hay contenido, no guardamos (mantiene firma existente)
    }
    setFirmaFullscreen(false);
    document.body.style.overflow = 'unset';
    // Desbloquear orientación y salir de fullscreen
    try {
      if (screen.orientation && screen.orientation.unlock) {
        screen.orientation.unlock();
      }
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      }
    } catch (e) { /* ok */ }
  };

  const limpiarFirmaFullscreen = () => {
    const canvas = fullCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  };

  // Inicializar canvas fullscreen
  useEffect(() => {
    if (!firmaFullscreen) return;
    // Calcular dimensiones - minimizar overhead para maximizar canvas
    const calcDims = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Header ~44px + botones ~56px + padding 16px = ~116px de overhead
      const w = vw - 16; // 8px padding a cada lado
      const h = vh - 116;
      setFullScreenDims({ width: Math.max(w, 280), height: Math.max(h, 150) });
    };
    calcDims();

    const handleResize = () => calcDims();
    window.addEventListener('resize', handleResize);
    // Escuchar cambio de orientación también
    window.addEventListener('orientationchange', handleResize);

    // Dibujar firma existente en el canvas fullscreen tras un pequeño delay
    setTimeout(() => {
      const canvas = fullCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (form.firma_cliente) {
        const img = new window.Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          // Escalar proporcionalmente y centrar
          const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
          const w = img.width * scale;
          const h = img.height * scale;
          const x = (canvas.width - w) / 2;
          const y = (canvas.height - h) / 2;
          ctx.drawImage(img, x, y, w, h);
        };
        img.src = form.firma_cliente;
      }
    }, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [firmaFullscreen]);

  // Inicializar canvas pequeño con fondo blanco
  useEffect(() => {
    if (seccionAbierta !== 'firma') {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (form.firma_cliente) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // Escalar proporcionalmente y centrar para no distorsionar
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        const x = (canvas.width - w) / 2;
        const y = (canvas.height - h) / 2;
        ctx.drawImage(img, x, y, w, h);
      };
      img.src = form.firma_cliente;
    }
  }, [form.firma_cliente, seccionAbierta]);

  // Función para cargar agendamientos
  async function cargarAgendamientos(clienteId) {
    try {
      if (!clienteId) {
        setAgendamientos([]);
        return;
      }
      
      setCargandoAgendamientos(true);
      const token = localStorage.getItem("token");
      
      // Obtener agendamientos del cliente (futuras y actuales)
      const res = await http.get(`/agendamientos-inteligentes/cliente/${clienteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrar solo agendamientos programados, pendientes, confirmados o en progreso
      const agendamientosFiltrados = (res.data || []).filter(
        ag => ["programado", "pendiente", "confirmado", "en_progreso", "en_proceso"].includes(ag.estado)
      );
      
      setAgendamientos(agendamientosFiltrados);
      setCargandoAgendamientos(false);
    } catch (err) {
      console.error("Error cargando agendamientos:", err);
      setAgendamientos([]);
      setCargandoAgendamientos(false);
    }
  }

  async function cargarTickets(clienteId) {
    try {
      if (!clienteId) {
        setTicketsCliente([]);
        return;
      }
      
      setCargandoTickets(true);
      const token = localStorage.getItem("token");
      
      // Obtener tickets reales del nuevo endpoint
      const res = await http.get(`/tickets/`, {
        params: {
          cliente_id: clienteId
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const ticketsFiltrados = (res.data || []).filter(t => 
        t.estado === 'pendiente' || 
        t.estado === 'en_proceso' || 
        (form.ticket_origen_id && (t._id === form.ticket_origen_id || t.id === form.ticket_origen_id))
      );
      
      setTicketsCliente(ticketsFiltrados);
      setCargandoTickets(false);
    } catch (err) {
      console.error("Error cargando tickets:", err);
      setTicketsCliente([]);
      setCargandoTickets(false);
    }
  }

  const SectionHeader = ({ id, titulo, icono: Icono, isOpen }) => (
    <button
      type="button"
      onClick={() => setSeccionAbierta(isOpen ? null : id)}
      className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all duration-200 shadow-sm ${
        isOpen 
          ? 'bg-emerald-50 border-emerald-200 text-emerald-700 shadow-md' 
          : 'bg-white border-gray-100 text-gray-600 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isOpen ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
          <Icono className="w-5 h-5" />
        </div>
        <span className="font-semibold text-left text-base">{titulo}</span>
      </div>
      {isOpen ? <ChevronDown className="w-5 h-5 text-emerald-500" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 flex flex-col h-full w-full overflow-hidden animate-in slide-in-from-bottom duration-300">
      {/* Header Fijo */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 px-4 py-4 shadow-md shrink-0 flex items-center justify-between z-10">
        <div className="text-white">
          <h3 className="text-lg font-bold flex items-center gap-2">
            {orden ? <PenTool className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
            {orden ? "Editar Orden" : "Nueva Orden"}
          </h3>
          <p className="text-emerald-100 text-xs mt-0.5 opacity-90">
            {orden ? `#${orden.numero_orden || 'Sin número'}` : "Creando nuevo registro"}
          </p>
        </div>
        <button 
          onClick={onClose}
          className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-full transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Contenido Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24 custom-scrollbar">
          
          {/* Sección: Cliente y Fechas */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-75">
            <SectionHeader 
              id="cliente" 
              titulo="Cliente y Fechas" 
              icono={User}
              isOpen={seccionAbierta === 'cliente'} 
            />
            
            {seccionAbierta === 'cliente' && (
              <div className="mt-3 space-y-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm animate-in zoom-in-95 duration-200">
                {(user?.rol === "admin" || user?.rol === "tecnico") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Cliente <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      className="w-full"
                      placeholder="Seleccionar cliente..."
                      value={form.cliente_id || ""}
                      options={clientes.map(c => ({ value: c.id || c._id, label: c.nombre }))}
                      onChange={(val) => handleClienteChange(val)}
                    />
                  </div>
                )}

                {/* Selector de Sucursal */}
                {form.cliente_id && (
                  <div className="animate-in fade-in duration-300">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Sucursal
                      {sucursales.length > 0 && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <select
                      disabled={sucursales.length === 0}
                      className={`w-full border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${sucursales.length === 0 ? 'bg-gray-100 text-gray-400' : 'bg-gray-50'}`}
                      value={form.sucursal || ""}
                      onChange={(e) => {
                        const sucursalSeleccionada = e.target.value;
                        console.log('🏢 Sucursal seleccionada:', sucursalSeleccionada);
                        setForm({ ...form, sucursal: sucursalSeleccionada, equipos_ids: [], departamentos: [] });
                        // Filtrar equipos por la sucursal seleccionada
                        filtrarEquiposPorSucursal(sucursalSeleccionada, []);
                      }}
                    >
                      <option value="">-- Seleccionar sucursal --</option>
                      {sucursales.map((sucursal) => (
                        <option key={sucursal} value={sucursal}>
                          {sucursal}
                        </option>
                      ))}
                    </select>
                    {sucursales.length > 0 && (
                      <small className="text-gray-500 text-xs mt-1 block px-1">
                        {sucursales.length} sucursales disponibles
                      </small>
                    )}
                  </div>
                )}

                {/* Departamentos / Áreas (multi-select) */}
                {departamentosDisponibles.length > 0 && (() => {
                  const deptsFiltrados = form.sucursal
                    ? departamentosDisponibles.filter(d => !d.sucursal_nombre || d.sucursal_nombre === form.sucursal)
                    : departamentosDisponibles.filter(d => !d.sucursal_nombre);
                  if (deptsFiltrados.length === 0) return null;
                  return (
                    <div className="animate-in fade-in duration-300">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                        <Layers className="w-4 h-4" />
                        Departamentos / Áreas
                      </label>
                      <div className="border border-gray-200 rounded-lg p-2 max-h-40 overflow-y-auto bg-gray-50 space-y-1">
                        {deptsFiltrados.map((d, i) => (
                          <label key={i} className="flex items-center gap-2 px-2 py-1.5 hover:bg-white rounded cursor-pointer text-sm">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                              checked={(form.departamentos || []).includes(d.nombre)}
                              onChange={(e) => {
                                const current = form.departamentos || [];
                                let next;
                                if (e.target.checked) {
                                  next = [...current, d.nombre];
                                } else {
                                  next = current.filter(x => x !== d.nombre);
                                }
                                setForm({ ...form, departamentos: next });
                                filtrarEquiposPorSucursal(form.sucursal, next);
                              }}
                            />
                            {d.nombre}
                          </label>
                        ))}
                      </div>
                      {(form.departamentos || []).length > 0 && (
                        <small className="text-emerald-600 text-xs mt-1 block px-1">
                          {form.departamentos.length} seleccionado(s)
                        </small>
                      )}
                    </div>
                  );
                })()}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Fecha del Servicio
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      className="w-full border-gray-200 rounded-lg pl-10 pr-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50"
                      value={form.fecha}
                      onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                    />
                    <Calendar className="w-5 h-5 text-gray-400 absolute left-3 top-3.5 pointer-events-none" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Hora Inicio
                    </label>
                    <div className="relative">
                      <input
                        type="time"
                        className="w-full border-gray-200 rounded-lg pl-9 pr-2 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50"
                        value={form.hora_inicio}
                        onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })}
                      />
                      <Clock className="w-4 h-4 text-gray-400 absolute left-3 top-4 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Hora Fin
                    </label>
                    <div className="relative">
                      <input
                        type="time"
                        className="w-full border-gray-200 rounded-lg pl-9 pr-2 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50"
                        value={form.hora_fin}
                        onChange={(e) => setForm({ ...form, hora_fin: e.target.value })}
                      />
                      <Clock className="w-4 h-4 text-gray-400 absolute left-3 top-4 pointer-events-none" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Estado
                  </label>
                  <select
                    className="w-full border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50"
                    value={form.estado}
                    onChange={(e) => {
                      const nuevoEstado = e.target.value;
                      
                      // Si cambia a completada y hora_fin está vacía, auto-llenar con hora actual
                      if (nuevoEstado === 'completada' && !form.hora_fin) {
                        const now = new Date();
                        const horaActual = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                        console.log('⏰ Auto-completando hora_fin:', horaActual);
                        setForm({ ...form, estado: nuevoEstado, hora_fin: horaActual });
                      } else {
                        setForm({ ...form, estado: nuevoEstado });
                      }
                    }}
                  >
                    <option value="borrador">📝 Borrador</option>
                    <option value="programada">📅 Programada</option>
                    <option value="en_proceso">🔄 En Proceso</option>
                    <option value="completada">✅ Completada</option>
                    <option value="cancelada">❌ Cancelada</option>
                  </select>
                </div>

                {/* Origen de la Orden */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Origen de la Orden
                  </label>
                  <select
                    className={`w-full border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${!form.cliente_id ? 'bg-gray-100 text-gray-400' : 'bg-gray-50'}`}
                    value={form.origen || "interno"}
                    disabled={!form.cliente_id}
                    onChange={(e) => {
                      const nuevoOrigen = e.target.value;
                      setForm({ 
                        ...form, 
                        origen: nuevoOrigen,
                        ticket_origen_id: nuevoOrigen !== 'ticket' ? "" : form.ticket_origen_id
                      });
                      
                      if (nuevoOrigen === 'ticket' && form.cliente_id && ticketsCliente.length === 0) {
                        cargarTickets(form.cliente_id);
                      }
                    }}
                  >
                    <option value="interno">Interno (Creada por la empresa)</option>
                    <option value="ticket">Ticket de Soporte (Solicitada por cliente)</option>
                  </select>
                  {!form.cliente_id && (
                    <p className="text-xs text-gray-500 mt-1 px-1">
                      Seleccione un cliente para cambiar el origen.
                    </p>
                  )}
                </div>

                {/* Selector de Ticket (Visible solo si origen es ticket) */}
                {form.origen === 'ticket' && (
                  <div className="animate-in slide-in-from-top-2 duration-200">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Seleccionar Ticket
                    </label>
                    <select
                      className="w-full border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50"
                      value={form.ticket_origen_id || ""}
                      onChange={(e) => {
                        const ticketId = e.target.value;
                        const ticket = ticketsCliente.find(t => t._id === ticketId || t.id === ticketId);
                        
                        if (ticket) {
                          setForm({
                            ...form,
                            ticket_origen_id: ticketId,
                            descripcion_problema: ticket.descripcion_problema || form.descripcion_problema,
                            sucursal: ticket.sucursal || form.sucursal,
                            equipos_ids: ticket.equipo_id ? [ticket.equipo_id] : form.equipos_ids,
                            equipo_descripcion: ticket.equipo_descripcion || form.equipo_descripcion,
                            observaciones: ticket.usuario_afectado 
                              ? (form.observaciones ? `${form.observaciones}\nUsuario afectado: ${ticket.usuario_afectado}` : `Usuario afectado: ${ticket.usuario_afectado}`)
                              : form.observaciones
                          });
                          
                          if (ticket.sucursal) {
                              filtrarEquiposPorSucursal(ticket.sucursal, form.departamentos);
                          }
                        } else {
                          setForm({
                            ...form,
                            ticket_origen_id: ticketId
                          });
                        }
                      }}
                      disabled={cargandoTickets}
                    >
                      <option value="">-- Seleccionar ticket --</option>
                      {cargandoTickets ? (
                        <option value="" disabled>Cargando tickets...</option>
                      ) : ticketsCliente.length === 0 ? (
                        <option value="" disabled>No hay tickets pendientes</option>
                      ) : (
                        ticketsCliente.map((t) => (
                          <option key={t._id || t.id} value={t._id || t.id}>
                            Ticket - {t.numero_ticket} - {t.sucursal || 'Sin sucursal'} - {new Date(t.fecha_creacion).toLocaleDateString()}
                          </option>
                        ))
                      )}
                    </select>
                    {ticketsCliente.length === 0 && !cargandoTickets && (
                      <small className="text-amber-600 text-xs block mt-1 px-1">
                        No se encontraron tickets pendientes para este cliente.
                      </small>
                    )}
                  </div>
                )}

                {/* Tipo de Servicio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Tipo de Servicio
                  </label>
                  <select
                    className="w-full border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50"
                    value={form.tipo_servicio || "mantenimiento_preventivo"}
                    onChange={(e) => {
                      const tipoSeleccionado = e.target.value;
                      setForm({
                        ...form,
                        tipo_servicio: tipoSeleccionado,
                        es_mantenimiento_preventivo: tipoSeleccionado === "mantenimiento_preventivo"
                      });
                    }}
                  >
                    <option value="mantenimiento_preventivo">Mantenimiento Preventivo</option>
                    <option value="correctivo">Mantenimiento Correctivo</option>
                    <option value="remoto">Servicio Remoto</option>
                    <option value="instalacion">Instalación</option>
                    <option value="reparacion">Reparación</option>
                    <option value="revision">Revisión</option>
                    <option value="diagnostico">Diagnóstico</option>
                    <option value="actualizacion">Actualización</option>
                    <option value="calibracion">Calibración</option>
                  </select>
                </div>

                {/* Servicio Programado - Checkbox */}
                <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-100">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="es_servicio_programado_mobile"
                      checked={form.es_servicio_programado || false}
                      onChange={async (e) => {
                        const isChecked = e.target.checked;
                        setForm({ 
                          ...form, 
                          es_servicio_programado: isChecked,
                          agendamiento_id: isChecked ? form.agendamiento_id : ""
                        });
                        
                        // Cargar agendamientos si se activa y hay cliente seleccionado
                        if (isChecked && form.cliente_id && agendamientos.length === 0) {
                          const clienteActual = clientes.find((c) => (c._id === form.cliente_id) || (c.id === form.cliente_id));
                          const clienteIdParaAgendamientos = clienteActual?.id || form.cliente_id;
                          await cargarAgendamientos(clienteIdParaAgendamientos);
                        }
                      }}
                      className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <label htmlFor="es_servicio_programado_mobile" className="text-sm font-medium text-gray-800">
                      ¿Es un servicio programado?
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 ml-8">
                    Marque si proviene de un agendamiento preventivo
                  </p>
                </div>

                {/* Selector de Agendamiento (solo si es servicio programado) */}
                {form.es_servicio_programado && (
                  <div className="animate-in slide-in-from-top-2 duration-200">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Agendamiento
                    </label>
                    <select
                      className="w-full border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50"
                      value={form.agendamiento_id || ""}
                      onChange={(e) => {
                        const agendamientoId = e.target.value;
                        const agendamiento = agendamientos.find(ag => ag._id === agendamientoId);
                        
                        setForm({
                          ...form,
                          agendamiento_id: agendamientoId,
                          sucursal: agendamiento?.sucursal || form.sucursal
                        });
                        
                        // Pre-seleccionar equipos del agendamiento
                        if (agendamiento?.equipos && Array.isArray(agendamiento.equipos)) {
                          setForm(prev => ({
                            ...prev,
                            equipos_ids: agendamiento.equipos
                          }));
                        }
                      }}
                      disabled={!form.cliente_id || cargandoAgendamientos}
                    >
                      <option value="">-- Seleccionar agendamiento --</option>
                      {cargandoAgendamientos ? (
                        <option value="" disabled>Cargando agendamientos...</option>
                      ) : agendamientos.length === 0 ? (
                        <option value="" disabled>No hay agendamientos disponibles</option>
                      ) : (
                        agendamientos.map((ag) => (
                          <option key={ag._id} value={ag._id}>
                            {ag.titulo || 'Mantenimiento'} - {ag.sucursal || 'Sin sucursal'} - {new Date(ag.fecha_programada).toLocaleDateString()} 
                            {ag.equipos?.length ? ` (${ag.equipos.length} equipos)` : ''}
                          </option>
                        ))
                      )}
                    </select>
                    {!form.cliente_id && (
                      <small className="text-amber-600 text-xs block mt-1 px-1">
                        Seleccione primero un cliente para ver sus agendamientos
                      </small>
                    )}
                    {form.cliente_id && agendamientos.length === 0 && !cargandoAgendamientos && (
                      <small className="text-gray-500 text-xs block mt-1 px-1">
                        Este cliente no tiene agendamientos programados disponibles
                      </small>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sección: Equipos */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
            <SectionHeader 
              id="equipos" 
              titulo="Equipos del Servicio" 
              icono={Monitor}
              isOpen={seccionAbierta === 'equipos'} 
            />
            
            {seccionAbierta === 'equipos' && (
              <div className="mt-3 space-y-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm animate-in zoom-in-95 duration-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex justify-between items-center">
                    Equipos Seleccionados
                    <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                      {form.equipos_ids.length}
                    </span>
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar border border-gray-100 rounded-lg p-1">
                    {equipos.map(equipo => (
                      <label key={equipo._id || equipo.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 active:bg-emerald-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={form.equipos_ids.includes(equipo._id || equipo.id)}
                          onChange={(e) => {
                            const equipoId = equipo._id || equipo.id;
                            let newDepts = form.departamentos || [];
                            if (e.target.checked) {
                              // Auto-detectar departamento
                              if (equipo.departamento && !newDepts.includes(equipo.departamento)) {
                                newDepts = [...newDepts, equipo.departamento];
                              }
                              setForm(prev => ({
                                ...prev,
                                equipos_ids: [...prev.equipos_ids, equipoId],
                                departamentos: newDepts
                              }));
                            } else {
                              setForm(prev => ({
                                ...prev,
                                equipos_ids: prev.equipos_ids.filter(id => id !== equipoId)
                              }));
                            }
                          }}
                          className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{equipo.nombre || 'Sin nombre'}</div>
                          <div className="text-xs text-gray-500">
                            {[equipo.tipo_equipo || 'Sin tipo', equipo.departamento].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                      </label>
                    ))}
                    {equipos.length === 0 && (
                      <div className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-lg border border-dashed border-gray-200">
                        {form.cliente_id ? "No hay equipos registrados" : "Selecciona un cliente primero"}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Descripción de Equipos
                  </label>
                  <textarea
                    className="w-full border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50 resize-none"
                    placeholder="Describe los equipos y componentes específicos..."
                    rows="3"
                    value={form.equipo_descripcion}
                    onChange={(e) => setForm({ ...form, equipo_descripcion: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sección: Trabajo Realizado */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-150">
            <SectionHeader 
              id="trabajo" 
              titulo="Trabajo Realizado" 
              icono={Wrench}
              isOpen={seccionAbierta === 'trabajo'} 
            />
            
            {seccionAbierta === 'trabajo' && (
              <div className="mt-3 space-y-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm animate-in zoom-in-95 duration-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Trabajos Realizados
                  </label>
                  <textarea
                    className="w-full border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50 resize-none"
                    placeholder="Describe detalladamente los trabajos realizados..."
                    rows="5"
                    value={form.trabajos_realizados}
                    onChange={(e) => setForm({ ...form, trabajos_realizados: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Observaciones
                  </label>
                  <textarea
                    className="w-full border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50 resize-none"
                    placeholder="Observaciones adicionales..."
                    rows="3"
                    value={form.observaciones}
                    onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sección: Técnico */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
            <SectionHeader 
              id="tecnico" 
              titulo="Técnico Asignado" 
              icono={User}
              isOpen={seccionAbierta === 'tecnico'} 
            />
            
            {seccionAbierta === 'tecnico' && (
              <div className="mt-3 space-y-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm animate-in zoom-in-95 duration-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Técnico Responsable
                  </label>
                  <select
                    className="w-full border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50"
                    value={form.tecnico_id}
                    onChange={(e) => {
                      const tecnico = tecnicos.find(t => t.id === e.target.value || t._id === e.target.value);
                      setForm({ 
                        ...form, 
                        tecnico_id: e.target.value,
                        nombre_tecnico: tecnico ? buildDisplayNameFromUser(tecnico) : "",
                        tecnico_nombre: tecnico ? buildDisplayNameFromUser(tecnico) : ""
                      });
                    }}
                  >
                    <option value="">-- Seleccionar técnico --</option>
                    {tecnicos.map(tecnico => (
                      <option key={tecnico.id || tecnico._id} value={tecnico.id || tecnico._id}>
                        {buildDisplayNameFromUser(tecnico) || tecnico.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Sección: Firma del Cliente */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300">
            <SectionHeader 
              id="firma" 
              titulo="Firma del Cliente" 
              icono={FileText}
              isOpen={seccionAbierta === 'firma'} 
            />
            
            {seccionAbierta === 'firma' && (
              <div className="mt-3 space-y-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm animate-in zoom-in-95 duration-200">
                {/* Canvas de firma táctil para móvil */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Firma del Cliente
                    </label>
                    <button
                      type="button"
                      onClick={abrirFirmaFullscreen}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 active:bg-emerald-200 transition-colors"
                    >
                      <Maximize2 className="h-4 w-4" />
                      Ampliar
                    </button>
                  </div>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-2 bg-gray-50">
                    <div className="bg-white rounded-lg overflow-hidden shadow-sm">
                      <canvas
                        ref={canvasRef}
                        width={300}
                        height={180}
                        className="w-full h-[180px] touch-none"
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                      />
                    </div>
                    <div className="flex justify-between mt-3 px-1">
                      <button
                        type="button"
                        onClick={abrirFirmaFullscreen}
                        className="text-sm font-medium text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg hover:bg-emerald-100 transition-colors inline-flex items-center gap-1"
                      >
                        <Maximize2 className="h-3.5 w-3.5" />
                        Pantalla Completa
                      </button>
                      <button
                        type="button"
                        onClick={limpiarFirma}
                        className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        Limpiar
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Aclaración de Firma
                  </label>
                  <input
                    type="text"
                    className="w-full border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50"
                    placeholder="Nombre completo y cargo"
                    value={form.firma_aclaracion}
                    onChange={(e) => setForm({ ...form, firma_aclaracion: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

      </div>

      {/* Footer con Botón de Guardar */}
      <div className="bg-white border-t border-gray-200 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] shrink-0 z-20">
        <button
          type="button"
          onClick={guardarOrden}
          disabled={guardando}
          className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white px-4 py-3.5 rounded-xl font-semibold text-lg shadow-lg shadow-emerald-200 hover:bg-emerald-700 active:scale-[0.98] disabled:opacity-70 disabled:scale-100 transition-all"
        >
          {guardando ? <Loader2 className="w-6 h-6 animate-spin" /> : <Save className="w-6 h-6" />}
          {guardando ? "Guardando..." : "Guardar Orden"}
        </button>
      </div>

      {/* Modal de firma pantalla completa - optimizado para landscape */}
      {firmaFullscreen && (
        <div className="fixed inset-0 z-[9999] bg-white flex flex-col">
          {/* Header compacto con instrucción + botón cerrar */}
          <div className="flex items-center justify-between px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shrink-0">
            <div className="flex items-center gap-2">
              <PenTool className="h-4 w-4" />
              <span className="font-semibold text-sm">Firma del Cliente</span>
              <span className="text-emerald-200 text-xs hidden sm:inline">— Gire el celular en horizontal para más espacio</span>
            </div>
            <button
              type="button"
              onClick={() => cerrarFirmaFullscreen(false)}
              className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Instrucción para portrait (visible solo en portrait) */}
          <div className="px-3 py-1 bg-emerald-50 text-emerald-600 text-xs text-center shrink-0 landscape:hidden">
            📱 Gire el celular en horizontal para firmar más cómodo
          </div>

          {/* Canvas área - máximo espacio posible */}
          <div className="flex-1 p-2 flex items-center justify-center bg-gray-50 overflow-hidden">
            <div className="w-full h-full border-2 border-dashed border-gray-300 rounded-xl bg-white shadow-inner overflow-hidden">
              <canvas
                ref={fullCanvasRef}
                width={fullScreenDims.width || 300}
                height={fullScreenDims.height || 300}
                className="w-full h-full touch-none"
                style={{ touchAction: 'none' }}
                onTouchStart={startDrawingFull}
                onTouchMove={drawFull}
                onTouchEnd={stopDrawingFull}
                onMouseDown={startDrawingFull}
                onMouseMove={drawFull}
                onMouseUp={stopDrawingFull}
                onMouseLeave={stopDrawingFull}
              />
            </div>
          </div>

          {/* Botones compactos */}
          <div className="flex gap-2 px-3 py-2 bg-white border-t border-gray-200 shrink-0">
            <button
              type="button"
              onClick={limpiarFirmaFullscreen}
              className="flex items-center justify-center gap-1.5 px-3 py-2 text-red-600 bg-red-50 rounded-lg text-sm font-medium border border-red-200 hover:bg-red-100 active:scale-[0.98] transition-all"
            >
              <X className="h-4 w-4" />
              Limpiar
            </button>
            <button
              type="button"
              onClick={() => cerrarFirmaFullscreen(false)}
              className="flex items-center justify-center gap-1.5 px-3 py-2 text-gray-600 bg-gray-100 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-200 active:scale-[0.98] transition-all"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => cerrarFirmaFullscreen(true)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-white bg-green-600 rounded-lg text-sm font-semibold shadow-md hover:bg-green-700 active:scale-[0.98] transition-all"
            >
              <Save className="h-4 w-4" />
              Aceptar Firma
            </button>
          </div>
        </div>
      )}
    </div>
  );
}